import { useCallback } from 'react'

import { encode } from '@utils/base64url'
import { getAppConfig } from '@utils/getAppConfig'
import { getLoanPurposeEncoded } from '@utils/getLoanPurposeEncoded'
import { getMarketingTrackingData } from '@utils/getMarketingTrackingData'
import { getScoreIdParts } from '@utils/getScoreIdParts'
import getTopLoansEventData from '@utils/getTopThreeLoansEventData'

import { ScoreId } from '@appTypes/App.interface'
import {
    CardsDownsellProduct,
    LoanProductCategory,
} from '@appTypes/LoansApi.interface'
import { ScoredLoanProduct } from '@appTypes/PagesApi.interface'

import { KbaScenario } from '@config/kbaScenarioKeys'

import useAppQueryState from './useAppQueryState'
import { useBasin } from './useBasin'
import useFeatureFlags from './useFeatureFlags'
import useGoogleTagManager from './useGoogleTagManager'
import { getVariationsFromFeatureFlags } from './utils/getVariationsFromFeatureFlags'

const useLogEvents = () => {
    const appConfig = getAppConfig()
    const { send, ids } = useBasin()
    const { appQueryState } = useAppQueryState()
    const gtmSend = useGoogleTagManager()
    const { featureFlags } = useFeatureFlags()

    const trackingData = getMarketingTrackingData(appQueryState)

    const logVisitEvent = useCallback(
        ({ category }: { category: string }) => {
            const variations = getVariationsFromFeatureFlags(featureFlags)

            const eventData = {
                Url: window.location.href,
                Referrer: document.referrer,
                UserAgent: navigator.userAgent,
                Whitelabel: appConfig.NAME_WHITELABEL,
                Variation: variations.join(','),
                Category: category,
                ScoreId: appQueryState.scoreId!,
                CustomerId: appQueryState.customerId!,
                CustomerType: appQueryState.customerType!,
                IsKnownCustomer: appQueryState.isKnownCustomer!,

                SendId: trackingData.sendId,
                mediasource: trackingData.m,
                campaign: trackingData.cam,
                keyword: trackingData.k,
                googleClickId: trackingData.gclid,
                adgroup: trackingData.adg,
            }

            send('Visit', eventData)
        },
        [send, appQueryState, trackingData]
    )

    const logLoansLoadedEvent = useCallback(() => {
        send('LoansLoaded', {
            Url: window.location.href,
            CustomerId: appQueryState.customerId!,
            Campaign: trackingData.cam,
        })
    }, [send, appQueryState])

    const logCheckEligibilityEvent = useCallback(
        ({
            category,
            loanAmount,
            loanTerm,
            origin,
        }: {
            origin: string
            category: string
            loanAmount: number
            loanTerm: number
        }) => {
            send('FormOpen', {
                Origin: origin,
                Category: category,
                LoanAmount: loanAmount,
                LoanTerm: loanTerm,
                Whitelabel: appConfig.NAME_WHITELABEL,
                ApiClientName: appConfig.NAME_WHITELABEL,
                customerId: appQueryState.customerId!,
                customerType: appQueryState.customerType!,
            })
        },
        [send]
    )

    const logModalFormOpenEvent = useCallback(
        ({
            isPrePopulated,
            productCode,
            row,
        }: {
            isPrePopulated: boolean
            productCode: string
            row: number
        }) => {
            send('ModalOpen', {
                CustomerId: appQueryState.customerId!,
                ProductCode: productCode,
                Referrer: document.referrer,
                url: window.location.href,
                isPrePopulated,
            })
        },
        [send]
    )

    const logModalFormCloseEvent = useCallback(
        ({
            productCode,
            row,
        }: {
            isPrePopulated: boolean
            productCode: string
            row: number
        }) => {
            send('ModalClose', {
                CustomerId: appQueryState.customerId!,
                ProductCode: productCode,
                Referrer: document.referrer,
                url: window.location.href,
            })
        },
        [send]
    )

    const logBannerKbaDisplayEvent = useCallback(
        ({ scenario }: { scenario: KbaScenario }) => {
            send('BannerKbaDisplay', {
                customerId: appQueryState.customerId,
                url: document.URL,
                scenario,
            })
        },
        [appQueryState.customerId, send]
    )

    const logBannerKbaClickEvent = useCallback(
        ({ scenario }: { scenario: KbaScenario }) => {
            send('BannerKbaClick', {
                customerId: appQueryState.customerId,
                scenario,
            })
        },
        [appQueryState.customerId, send]
    )

    const logLoansResultsEmailTriggerEvent = useCallback(
        ({
            loans,
            loanPurpose,
        }: {
            loans: ScoredLoanProduct[] | null
            loanPurpose: string
        }) => {
            const topLoans = getTopLoansEventData(
                loans?.filter(loan => !loan.isSponsored) ?? []
            )

            if (topLoans == null || typeof window === 'undefined') {
                return
            }

            const { leadId, loanAmount, loanTerm } = getScoreIdParts(
                appQueryState.scoreId! as ScoreId
            )

            send('ResultsEmailTriggerV1', {
                ScoreId: appQueryState.scoreId!,
                ScoreIdEncoded: encode(appQueryState.scoreId!),
                LeadId: leadId,
                CustomerId: appQueryState.customerId!,
                Url: window.location.href,

                // TODO: Check with CRM if this value is used
                //WhitelabelDeepLinkUrl: getWhitelabelDeepLinkUrl(accountType),

                LoanAmount: loanAmount,
                LoanTerm: loanTerm,
                LoanPurpose: loanPurpose,
                LoanPurposeEncoded: getLoanPurposeEncoded(loanPurpose),
                ...topLoans,
            })
        },
        [appQueryState.scoreId, appQueryState.customerId]
    )

    const logScoredTableUpdateEvent = useCallback(
        ({
            category,
            sortedBy,
            products,
        }: {
            category: LoanProductCategory
            sortedBy: string
            products: ScoredLoanProduct[]
        }) => {
            const { loanAmount, loanTerm } = getScoreIdParts(
                appQueryState.scoreId! as ScoreId
            )

            const sortedProducts = products.map(product => {
                return {
                    productCode: product.loanId,
                    isSponsored: product.isSponsored,
                    hasMarketingOffer: product.hasMarketingOffer,
                }
            })

            send('ScoredTableUpdate', {
                customerId: appQueryState.customerId,
                category,
                loanAmount,
                loanTerm,
                sortedBy,
                sortedProducts,
            })
        },
        [appQueryState.customerId, send]
    )

    const logSortChangeEvent = useCallback(
        ({ sortKey }: { sortKey: string }) => {
            send('Sort', {
                SortBy: sortKey,
                ApiClientName: appConfig.NAME_WHITELABEL,
                CustomerId: appQueryState.customerId!,
                CustomerType: appQueryState.customerType!,
            })
        },
        [send]
    )

    const logCategoryChangeEvent = useCallback(
        ({
            fromCategory,
            toCategory,
        }: {
            fromCategory: LoanProductCategory
            toCategory: LoanProductCategory
        }) => {
            send('ChangeTab', {
                existingCategory: fromCategory,
                category: toCategory,
                customerId: appQueryState.customerId,
                whitelabel: appConfig.NAME_WHITELABEL,
            })
        },
        [send]
    )

    const logResultsUpdateEvent = useCallback(
        ({
            loanAmount,
            loanTerm,
            loanPurpose,
        }: {
            loanAmount: number
            loanTerm: number
            loanPurpose?: string
        }) => {
            send('ResultsUpdate', {
                LoanAmount: loanAmount,
                LoanTerm: loanTerm,
                LoanPurpose: loanPurpose,
                ApiClientName: appConfig.NAME_WHITELABEL,
                CustomerId: appQueryState.customerId,
                CustomerType: appQueryState.customerType,
            })
        },
        [send]
    )

    const logPostClickEvent = useCallback(
        ({
            productCode,
            row,
            score,
            monthlyRepayment,
            totalChargeForCredit,
            totalAmountRepayable,
            representativeApr,
            isBestMatch,
            category,
        }: {
            productCode: string
            row: number
            score?: number
            monthlyRepayment?: number
            totalChargeForCredit?: number
            totalAmountRepayable?: number
            representativeApr?: number
            isBestMatch?: boolean
            category?: string
        }) => {
            send('PostClick', {
                ProductCode: productCode,
                Row: row,
                Score: score,
                MonthlyRepayment: monthlyRepayment,
                TotalChargeForCredit: totalChargeForCredit,
                TotalAmountRepayable: totalAmountRepayable,
                RepresentativeApr: representativeApr,
                CustomerId: appQueryState.customerId,
                CustomerType: appQueryState.customerType,
                IsKnownCustomer: appQueryState.isKnownCustomer,
                ApiClientName: appConfig.NAME_WHITELABEL,
                googleClickId: trackingData.gclid,
                BestMatch: isBestMatch,
                Category: category,
            })
        },
        [send]
    )

    const logSecuredFormOpenEvent = useCallback(
        ({ loan, row }: { loan: ScoredLoanProduct; row: number }) => {
            send('SecuredFormOpen', {
                ProductCode: loan.loanId,
                Row: row,
                MonthlyRepayment: loan.monthlyPayment,
                TotalChargeForCredit: loan.totalCharge,
                TotalAmountRepayable: loan.totalRepayable,
                RepresentativeApr: loan.apr,
                score: loan.score,
                Broker:
                    loan.productDetails.lender === 'Norton'
                        ? 'Norton'
                        : 'Fluent',
                ApiClientName: appConfig.NAME_WHITELABEL,
            })
        },
        [send]
    )

    const logSecuredFormValidationEvent = useCallback(
        ({
            hasPhoneNumber,
            isValid,
        }: {
            hasPhoneNumber: boolean
            isValid: boolean
        }) => {
            send('SecuredFormValidation', {
                PhoneNumber: hasPhoneNumber,
                Valid: isValid,
            })
        },
        [send]
    )

    const logSecuredFormSubmitEvent = useCallback(() => {
        send('SecuredFormSubmit', {
            CustomerId: appQueryState.customerId,
            partner: appConfig.NAME_WHITELABEL,
            ApiClientName: appConfig.NAME_WHITELABEL,
        })
    }, [send])

    const logCardsDownsellClickEvent = useCallback(
        ({
            cardToShow,
            numberOfOptions,
        }: {
            cardToShow: CardsDownsellProduct
            numberOfOptions: number
        }) => {
            send('DownsellClick', {
                VisitGuid: ids?.sessionId,
                DailyId: ids?.dailyId,
                PermanentId: ids?.permanentId,
                CardProductCode: cardToShow.productCode,
                Score: cardToShow.score,
                CustomerId: appQueryState.customerId!,
                NumberOfOptions: numberOfOptions,
                DownsellCategory: 'card',
            })
        },
        [send]
    )

    const logCardsDownsellShownEvent = useCallback(() => {
        send('CardsDownsellShown', {
            VisitGuid: ids?.sessionId,
            DailyId: ids?.dailyId,
            PermanentId: ids?.permanentId,
            CardsDownsellShown: true,
            CustomerId: appQueryState.customerId!,
            CustomerType: appQueryState.customerType!,
        })
    }, [send])

    const logEligibilityFactorsCardStatusEvent = useCallback(
        ({
            numberOfEligibilityFactors,
            isOnEligibilityFactorsCardVariation,
        }: {
            numberOfEligibilityFactors: number
            isOnEligibilityFactorsCardVariation: boolean
        }) => {
            send('EligibilityFactorsCardStatus', {
                eligibilityFactorsCount: numberOfEligibilityFactors,
                windowWidth: window.innerWidth,
                customerId: appQueryState.customerId!,
                isOnEligibilityFactorsCardVariation,
            })
        },
        [send]
    )

    const logEligibilityFactorsCardShownEvent = useCallback(
        ({
            numberOfEligibilityFactors,
        }: {
            numberOfEligibilityFactors: number
        }) => {
            send('EligibilityFactorsCardShown', {
                eligibilityFactorsCount: numberOfEligibilityFactors,
                customerId: appQueryState.customerId!,
            })
        },
        [send]
    )

    const logEligibilityFactorsCardDeepLinkClickEvent = useCallback(
        ({
            numberOfEligibilityFactors,
        }: {
            numberOfEligibilityFactors: number
        }) => {
            send('EligibilityFactorsCardDeepLinkClick', {
                eligibilityFactorsCount: numberOfEligibilityFactors,
                customerId: appQueryState.customerId!,
            })
        },
        [send]
    )

    const logAlternativeProductsVisit = useCallback(
        ({
            cardsDownsellShown,
            hasGuarantorLoans,
        }: {
            cardsDownsellShown: boolean
            hasGuarantorLoans: boolean
        }) => {
            const { leadId, loanAmount, loanTerm } = getScoreIdParts(
                appQueryState.scoreId! as ScoreId
            )
            const variations = getVariationsFromFeatureFlags(featureFlags)

            send('AlternativeProductsVisit', {
                VisitGuid: ids?.sessionId,
                DailyId: ids?.dailyId,
                PermanentId: ids?.permanentId,
                Variation: variations.join(','),
                CardsDownsellShown: cardsDownsellShown,
                NoGuarantor: hasGuarantorLoans,
                ScoreId: appQueryState.scoreId!,
                ScoreIdEncoded: encode(appQueryState.scoreId!),
                LeadId: leadId,
                CustomerId: appQueryState.customerId!,
                LoanAmount: loanAmount,
                LoanTerm: loanTerm,
                CustomerType: appQueryState.customerType!,
                ApiClientName: appConfig.NAME_WHITELABEL,
            })
        },
        [send]
    )

    return {
        logVisitEvent,

        logAlternativeProductsVisit,

        logLoansLoadedEvent,

        logCheckEligibilityEvent,

        logModalFormOpenEvent,

        logModalFormCloseEvent,

        logBannerKbaDisplayEvent,

        logBannerKbaClickEvent,

        logLoansResultsEmailTriggerEvent,

        logScoredTableUpdateEvent,

        logSortChangeEvent,

        logCategoryChangeEvent,

        logResultsUpdateEvent,

        logPostClickEvent,

        logSecuredFormOpenEvent,

        logSecuredFormValidationEvent,

        logSecuredFormSubmitEvent,

        logCardsDownsellClickEvent,

        logCardsDownsellShownEvent,

        logEligibilityFactorsCardStatusEvent,

        logEligibilityFactorsCardShownEvent,

        logEligibilityFactorsCardDeepLinkClickEvent,
    }
}

export default useLogEvents
